import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SimpleAuthProvider } from './context/SimpleAuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { SimpleProtectedRoute } from './components/SimpleProtectedRoute';
import MaterialLogin from './pages/MaterialLogin';
import MaterialRegister from './pages/auth/MaterialRegister';
import MaterialDashboard from './pages/MaterialDashboard';
import MaterialFrameworksPage from './pages/framework/MaterialFrameworksPage';
import MaterialFrameworkCreatePage from './pages/framework/MaterialFrameworkCreatePage';
import MaterialFrameworkDetailPage from './pages/framework/MaterialFrameworkDetailPage';
import MaterialFrameworkEditPage from './pages/framework/MaterialFrameworkEditPage';
import MaterialObligationsPage from './pages/obligation/MaterialObligationsPage';
import MaterialObligationDetailPage from './pages/obligation/MaterialObligationDetailPage';
import MaterialObligationCreatePage from './pages/obligation/MaterialObligationCreatePage';
import MaterialObligationEditPage from './pages/obligation/MaterialObligationEditPage';
import MaterialControlsPage from './pages/control/MaterialControlsPage';
import MaterialControlCreatePage from './pages/control/MaterialControlCreatePage';
import MaterialControlDetailPage from './pages/control/MaterialControlDetailPage';
import MaterialControlEditPage from './pages/control/MaterialControlEditPage';
import MaterialRequirementsPage from './pages/requirement/MaterialRequirementsPage';
import MaterialRequirementCreatePage from './pages/requirement/MaterialRequirementCreatePage';
import MaterialRequirementDetailPage from './pages/requirement/MaterialRequirementDetailPage';
import MaterialRequirementEditPage from './pages/requirement/MaterialRequirementEditPage';
import MaterialTasksPage from './pages/task/MaterialTasksPage';
import MaterialTaskCreate from './pages/task/MaterialTaskCreate';
import MaterialTaskDetail from './pages/task/MaterialTaskDetail';
import MaterialTaskEdit from './pages/task/MaterialTaskEdit';
import MaterialDocumentsPage from './pages/document/MaterialDocumentsPage';
import MaterialDocumentUploadPage from './pages/document/MaterialDocumentUploadPage';
import MaterialDocumentDetailPage from './pages/document/MaterialDocumentDetailPage';
import MaterialDocumentEditPage from './pages/document/MaterialDocumentEditPage';
import MaterialLayout from './components/MaterialLayout';
import MaterialUIWrapper from './components/MaterialUIWrapper';
import MaterialReportsPage from './pages/report/MaterialReportsPage';
import MaterialUsersPage from './pages/user/MaterialUsersPage';
import MaterialSettingsPage from './pages/settings/MaterialSettingsPage';
import MaterialRBACManagementPage from './pages/rbac/MaterialRBACManagementPage';

/**
 * Main application component for ModularCompliance
 * 
 * Uses HashRouter instead of BrowserRouter to avoid server configuration issues
 * Proper authentication handling with SimpleProtectedRoute
 * Clean routing without relying on router-fix.js
 */
export default function App() {
  console.log('[APP-DEBUG] App component rendering');
  
  return (
    <MaterialUIWrapper>
      <SimpleAuthProvider>
        <CompanyProvider>
          <Router>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<MaterialLogin />} />
            <Route path="/register" element={<MaterialRegister />} />
            
            {/* Index route redirects */}
            <Route path="/index" element={<Navigate to="/" replace />} />
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialDashboard />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Framework routes */}
            <Route
              path="/frameworks"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialFrameworksPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/frameworks/create"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialFrameworkCreatePage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/frameworks/:id"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialFrameworkDetailPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/frameworks/:id/edit"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialFrameworkEditPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Obligation routes */}
            <Route
              path="/obligations"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialObligationsPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/obligations/create"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialObligationCreatePage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/obligations/:id"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialObligationDetailPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/obligations/:id/edit"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialObligationEditPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Control routes */}
            <Route
              path="/controls"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialControlsPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/controls/create"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialControlCreatePage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/controls/:id"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialControlDetailPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/controls/:id/edit"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialControlEditPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Requirement routes */}
            <Route
              path="/requirements"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialRequirementsPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/requirements/create"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialRequirementCreatePage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/requirements/:id"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialRequirementDetailPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/requirements/:id/edit"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialRequirementEditPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Task routes */}
            <Route
              path="/tasks"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialTasksPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/tasks/create"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialTaskCreate />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/tasks/:id"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialTaskDetail />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/tasks/:id/edit"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialTaskEdit />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Document routes */}
            <Route
              path="/documents"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialDocumentsPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/documents/upload"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialDocumentUploadPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/documents/:id"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialDocumentDetailPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            <Route
              path="/documents/:id/edit"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialDocumentEditPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Reports route */}
            <Route
              path="/reports"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialReportsPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Users route */}
            <Route
              path="/users"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialUsersPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Settings route */}
            <Route
              path="/settings"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialSettingsPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* RBAC Management route */}
            <Route
              path="/rbac"
              element={
                <SimpleProtectedRoute>
                  <MaterialLayout>
                    <MaterialRBACManagementPage />
                  </MaterialLayout>
                </SimpleProtectedRoute>
              }
            />
            
            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        </CompanyProvider>
      </SimpleAuthProvider>
    </MaterialUIWrapper>
  );
}
